import { FloatingLabel, Form } from 'react-bootstrap';

interface EditTimeProps {
  /** Floating label + placeholder. Omit to render a bare control (the caller supplies its own label, e.g. via `Field`). */
  readonly label?: string;
  readonly value: string | null;
  readonly onChange: (value: string | null) => void;
  readonly mandatory?: boolean;
  /**
   * Whether the operator can enter seconds. Off by default: most times in this
   * app are minute-precision, and a seconds box the user has to tab past is
   * noise. Turn it on where the domain really is second-precise — a scheduling
   * window's open and close times are stored as `time` and evaluated to the
   * second, whereas a recurrence has no seconds at all.
   *
   * Drives the native picker's `step`: a step of 1 second makes the browser
   * render the seconds field and emit `HH:MM:SS`; the default step of 60 renders
   * minutes only and emits `HH:MM`.
   */
  readonly seconds?: boolean;
}

/**
 * Time-of-day input wrapper. Renders `<input type="time">` (browser native time
 * picker). Returns `null` for an empty value, otherwise the string the picker
 * emits — `HH:MM`, or `HH:MM:SS` with `seconds`. The backend accepts both shapes.
 * When `label` is omitted the bare control renders and the caller owns the label.
 */
export const EditTime = ({ label, value, onChange, mandatory = false, seconds = false }: EditTimeProps) => {
  const control = (
    <Form.Control
      type="time"
      step={seconds ? 1 : undefined}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      placeholder={label}
      required={mandatory}
    />
  );
  return label ? <FloatingLabel label={mandatory ? `${label} *` : label}>{control}</FloatingLabel> : control;
};
